import type { ChartData, ChartSeries } from '@/entities/dashboard';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Checks whether every category is an ISO date (YYYY-MM-DD), i.e. the chart is
 * grouped by date/week rather than by a categorical dimension.
 */
export const isDateCategories = (categories: string[]): boolean =>
  categories.length > 0 && categories.every((category) => ISO_DATE_PATTERN.test(category));

/**
 * Sorts date-grouped chart data chronologically (oldest first). The API returns
 * date categories newest-first; series data points are aligned with categories
 * by index, so both are reordered together. Categorical data is returned as-is.
 */
export const sortChartDataByDate = (data: ChartData): ChartData => {
  if (!isDateCategories(data.categories)) {
    return data;
  }

  const order = data.categories
    .map((category, index) => ({ category, index }))
    .sort((a, b) => a.category.localeCompare(b.category));

  return {
    ...data,
    categories: order.map((entry) => entry.category),
    series: data.series.map((series) => ({
      ...series,
      data: order.map((entry) => series.data[entry.index])
    }))
  };
};

/**
 * Returns per-series colors only when every series has a distinct color;
 * otherwise returns undefined for all so the ECharts palette assigns
 * distinguishable colors. Colors come from the frontend vehicle-category
 * palette (transport) or the API (storage charts); either source can leave
 * gaps or duplicates — e.g. unmapped categories, or legacy ids 1/4/7
 * sharing one color.
 */
export const resolveSeriesColors = (series: ChartSeries[]): (string | undefined)[] => {
  const colors = series.map((item) => item.color);
  const defined = colors.filter((color): color is string => Boolean(color));
  const allDistinct = defined.length === series.length && new Set(defined).size === defined.length;

  return allDistinct ? colors : series.map(() => undefined);
};
